import { useMemo, useState, useCallback } from "react";
import { Modal } from "@/components/base/Modal";
import * as productApi from "@/lib/api/products";
import { money } from "@/lib/format";
import {
  type ProductOption,
  type CombinationRow,
  type VariantStatus,
  countCombinations,
  combinationSafety,
  generateCombinations,
  findDuplicateCombinations,
  findDuplicateSkus,
  generateSkuFromPattern,
  validateSkuPattern,
  canonicalOptionSkuCode,
} from "@/lib/products";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

// Monotonic, deterministic ID for transient option rows (never persisted).
let optionIdSeq = 2;

interface Props {
  productId: string;
  orgId: string;
  productSku: string | null;
  productName: string;
  defaultCost: number | null;
  defaultSelling: number | null;
  defaultWeightKg: number | null;
  defaultLengthCm: number | null;
  defaultWidthCm: number | null;
  defaultHeightCm: number | null;
  reorderPoint: number;
  reorderQty: number;
  existingCount: number;
  existingSkus: string[];
  existingVariants: { id: string; sku: string | null; optionValues: Record<string, string> | null }[];
  onClose: () => void;
  onComplete: () => void;
}

function parseValues(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  text.split(/[,;\n]/).forEach((raw) => {
    const v = raw.trim();
    if (v && !seen.has(v.toLowerCase())) {
      seen.add(v.toLowerCase());
      out.push(v);
    }
  });
  return out;
}

// Generate a temporary UI-only ID for unsaved option values (never persisted)
function tmpId(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) { h = ((h << 5) - h + seed.charCodeAt(i)) | 0; }
  return `tmp-${Math.abs(h).toString(36)}`;
}

export default function VariantMatrixModalV2({
  productId,
  orgId,
  productSku,
  productName,
  defaultCost,
  defaultSelling,
  defaultWeightKg,
  defaultLengthCm,
  defaultWidthCm,
  defaultHeightCm,
  reorderPoint,
  reorderQty,
  existingCount,
  existingSkus,
  existingVariants: _existingVariants,
  onClose,
  onComplete,
}: Props) {
  const [options, setOptions] = useState<ProductOption[]>([
    { id: `opt-0`, org_id: orgId, product_id: productId, name: "Colour", position: 0, status: "active", values: [] },
    { id: `opt-1`, org_id: orgId, product_id: productId, name: "Size", position: 1, status: "active", values: [] },
  ]);

  const [skuPattern, setSkuPattern] = useState(
    productSku
      ? `${productSku.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8)}-{COLOUR}-{SIZE}`
      : "{NAME}-{COLOUR}-{SIZE}",
  );

  const [sharedCost, setSharedCost] = useState("");
  const [sharedSelling, setSharedSelling] = useState(defaultSelling != null ? String(defaultSelling) : "");
  const [sharedWeight, setSharedWeight] = useState(defaultWeightKg != null ? String(defaultWeightKg) : "");
  const [sharedLength, setSharedLength] = useState(defaultLengthCm != null ? String(defaultLengthCm) : "");
  const [sharedWidth, setSharedWidth] = useState(defaultWidthCm != null ? String(defaultWidthCm) : "");
  const [sharedHeight, setSharedHeight] = useState(defaultHeightCm != null ? String(defaultHeightCm) : "");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmRetiredCombinations, setConfirmRetiredCombinations] = useState(false);
  const [search, setSearch] = useState("");
  const [activeStep, setActiveStep] = useState<"options" | "preview" | "confirm">("options");
  const [page, setPage] = useState(0);
  const [bulkStatus, setBulkStatus] = useState<"active" | "draft" | "paused">("active");
  const [bulkDims, setBulkDims] = useState({ l: "", w: "", h: "" });

  const updateOptionName = (optId: string, name: string) => {
    setOptions((opts) => opts.map((o) => (o.id === optId ? { ...o, name } : o)));
  };

  // Raw draft per option so commas/spaces type naturally; parse on blur/Enter only.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const draftValue = (o: ProductOption) => drafts[o.id] ?? o.values.map((v) => v.value).join(", ");

  const updateOptionValues = (optId: string, text: string) => {
    const parsed = parseValues(text);
    setOptions((opts) =>
      opts.map((o) =>
        o.id === optId
          ? {
              ...o,
              values: parsed.map((v, i) => ({
                id: tmpId(`${o.name}:${v}`),
                org_id: orgId,
                product_option_id: optId,
                value: v,
                position: i,
                status: "active" as const,
              })),
            }
          : o,
      ),
    );
    setDrafts((d) => ({ ...d, [optId]: parsed.join(", ") }));
  };

  const addOption = () => {
    setOptions((opts) => [
      ...opts,
      { id: `opt-${optionIdSeq++}`, org_id: orgId, product_id: productId, name: "", position: opts.length, status: "active", values: [] },
    ]);
  };

  const removeOption = (optId: string) => {
    setOptions((opts) => {
      const filtered = opts.filter((o) => o.id !== optId);
      return filtered.map((o, i) => ({ ...o, position: i }));
    });
  };

  const parsedOptions = useMemo(
    () => options.filter((o) => o.name.trim().length > 0 && o.values.length > 0),
    [options],
  );

  const comboCount = useMemo(() => countCombinations(parsedOptions), [parsedOptions]);
  const safety = useMemo(() => combinationSafety(parsedOptions), [parsedOptions]);

  const [combinations, setCombinations] = useState<CombinationRow[]>([]);

  const buildPreview = useCallback(() => {
    setError("");
    const patternErr = validateSkuPattern(skuPattern, parsedOptions.map((o) => o.name));
    if (patternErr) { setError(patternErr); return; }
    if (!safety.safe) { setError(safety.warning ?? "Too many combinations."); return; }
    const combos = generateCombinations(parsedOptions, skuPattern, { productSku, productName }, existingSkus, existingCount);
    const sCost = sharedCost.trim() === "" ? null : Number(sharedCost);
    const sSell = sharedSelling.trim() === "" ? null : Number(sharedSelling);
    const sW = sharedWeight.trim() === "" ? null : Number(sharedWeight);
    const sL = sharedLength.trim() === "" ? null : Number(sharedLength);
    const sWi = sharedWidth.trim() === "" ? null : Number(sharedWidth);
    const sH = sharedHeight.trim() === "" ? null : Number(sharedHeight);
    setCombinations(combos.map((c) => ({ ...c, cost: sCost, price: sSell, weightKg: sW, lengthCm: sL, widthCm: sWi, heightCm: sH })));
    setActiveStep("preview");
  }, [parsedOptions, skuPattern, safety, existingSkus, existingCount, productSku, productName, sharedCost, sharedSelling, sharedWeight, sharedLength, sharedWidth, sharedHeight]);

  const toggleIncluded = (idx: number) => setCombinations((cs) => cs.map((c, i) => (i === idx ? { ...c, included: !c.included } : c)));
  const setComboField = (idx: number, field: keyof CombinationRow, value: unknown) =>
    setCombinations((cs) => cs.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));

  const includedCount = useMemo(() => combinations.filter((c) => c.included).length, [combinations]);
  const dupCombos = useMemo(() => findDuplicateCombinations(combinations), [combinations]);
  const dupSkus = useMemo(() => findDuplicateSkus(combinations, existingSkus), [combinations, existingSkus]);

  const bulkSetPrice = (val: string) => { const n = val.trim() === "" ? null : Number(val); setCombinations((cs) => cs.map((c) => ({ ...c, price: n }))); };
  const bulkSetCost = (val: string) => { const n = val.trim() === "" ? null : Number(val); setCombinations((cs) => cs.map((c) => ({ ...c, cost: n }))); };
  const bulkSetWeight = (val: string) => { const n = val.trim() === "" ? null : Number(val); setCombinations((cs) => cs.map((c) => ({ ...c, weightKg: n }))); };
  const bulkSetDims = (l: string, w: string, h: string) => {
    const nl = l.trim() === "" ? null : Number(l);
    const nw = w.trim() === "" ? null : Number(w);
    const nh = h.trim() === "" ? null : Number(h);
    setCombinations((cs) => cs.map((c) => ({ ...c, lengthCm: nl, widthCm: nw, heightCm: nh })));
  };
  const applyBulkDims = () => {
    const nl = bulkDims.l.trim() === "" ? null : Number(bulkDims.l);
    const nw = bulkDims.w.trim() === "" ? null : Number(bulkDims.w);
    const nh = bulkDims.h.trim() === "" ? null : Number(bulkDims.h);
    setCombinations((cs) => cs.map((c) => ({ ...c, lengthCm: nl, widthCm: nw, heightCm: nh })));
  };
  const bulkSetStatus = (val: string) => setCombinations((cs) => cs.map((c) => ({ ...c, status: val as VariantStatus })));
  const bulkActivate = () => setCombinations((cs) => cs.map((c) => ({ ...c, included: true })));
  const bulkDeactivate = () => setCombinations((cs) => cs.map((c) => ({ ...c, included: false })));
  const applyPatternToAll = () => {
    const patternErr = validateSkuPattern(skuPattern, parsedOptions.map((o) => o.name));
    if (patternErr) { setError(patternErr); return; }
    const used = new Set(existingSkus.map((s) => s.toUpperCase()));
    setCombinations((cs) => cs.map((c, idx) => {
      const codes: Record<string, string> = {};
      for (let i = 0; i < parsedOptions.length && i < c.values.length; i++) {
        codes[parsedOptions[i].name] = canonicalOptionSkuCode(c.values[i]);
      }
      let sku = generateSkuFromPattern(skuPattern, { productSku, productName, optionCodes: codes }, idx);
      let n = 2; const base = sku;
      while (used.has(sku.toUpperCase())) { sku = `${base}-${n++}`; }
      used.add(sku.toUpperCase());
      return { ...c, sku };
    }));
    setError("");
  };

  const filteredCombinations = useMemo(() => {
    if (!search.trim()) return combinations;
    const q = search.toLowerCase();
    return combinations.filter((c) => c.sku.toLowerCase().includes(q) || c.values.some((v) => v.toLowerCase().includes(q)));
  }, [combinations, search]);

  const PAGE_SIZE = 50;
  const totalPages = Math.max(1, Math.ceil(filteredCombinations.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pagedCombinations = filteredCombinations.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const handleCreate = async () => {
    setError("");
    if (parsedOptions.length === 0 || includedCount === 0) { setError("Define at least one option with values and include at least one combination."); return; }
    if (dupCombos.length > 0) { setError(`${dupCombos.length} duplicate combination(s) found. Resolve before creating.`); return; }
    if (dupSkus.length > 0) { setError(`${dupSkus.length} duplicate SKU(s) found. Resolve before creating.`); return; }

    // Build normalized options payload for RPC
    const optionsPayload = parsedOptions.map((o, pos) => ({
      name: o.name,
      position: pos,
      values: o.values.map((v, vPos) => ({ value: v.value, position: vPos })),
    }));

    const variantsPayload = combinations.filter((c) => c.included).map((c, idx) => {
      const optionValues: Record<string, string> = {};
      for (let i = 0; i < parsedOptions.length && i < c.values.length; i++) {
        optionValues[parsedOptions[i].name] = c.values[i];
      }
      return {
        sku: c.sku,
        barcode: c.barcode,
        option_values: optionValues,
        cost_price: c.cost,
        selling_price: c.price,
        weight_kg: c.weightKg,
        length_cm: c.lengthCm,
        width_cm: c.widthCm,
        height_cm: c.heightCm,
        reorder_point: reorderPoint,
        reorder_qty: reorderQty,
        position: existingCount + idx,
        status: c.status,
      };
    });

    if (variantsPayload.length === 0) { setError("No variants selected."); return; }

    setSaving(true);
    try {
      await productApi.createVariantMatrix(orgId, productId, optionsPayload, variantsPayload, confirmRetiredCombinations);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);

    onComplete();
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Bulk Add Variants"
      size="xl"
      footer={
        <div className="flex items-center gap-3">
          {activeStep === "options" && (
            <>
              <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
              <button onClick={buildPreview} disabled={parsedOptions.length === 0 || !safety.safe} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
                Preview {comboCount > 0 ? `${comboCount.toLocaleString()} combinations` : ""}
              </button>
            </>
          )}
          {activeStep === "preview" && (
            <>
              <button onClick={() => setActiveStep("options")} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Back to Options</button>
              <button onClick={() => setActiveStep("confirm")} disabled={includedCount === 0} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
                Review {includedCount.toLocaleString()} selected
              </button>
            </>
          )}
          {activeStep === "confirm" && (
            <>
              <button onClick={() => setActiveStep("preview")} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Back</button>
              <button onClick={handleCreate} disabled={saving || includedCount === 0} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
                {saving ? "Creating..." : `Create ${includedCount.toLocaleString()} variant${includedCount === 1 ? "" : "s"}`}
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
        {error.includes("RETIRED_COMBINATION_CONFIRMATION_REQUIRED") && (
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" className="mt-1" checked={confirmRetiredCombinations} onChange={(e) => setConfirmRetiredCombinations(e.target.checked)} />
            Recreate retired combinations with new canonical identities. This does not restore old stock, jobs or marketplace bindings.
          </label>
        )}

        {activeStep === "options" && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-xs text-emerald-700">
              <i className="ri-information-line mr-1" />
              Define unlimited option dimensions. Options and values are saved with stable database IDs — renames are safe and won&apos;t break inventory links.
            </div>
            <div>
              <label className={labelCls}>SKU pattern</label>
              <input value={skuPattern} onChange={(e) => setSkuPattern(e.target.value)} className={inputCls} placeholder="{SKU}-{COLOUR}-{SIZE}" />
              <p className="text-xs text-slate-400 mt-1">
                Tokens: {"{SKU}"}, {"{NAME}"}, {"{INDEX}"} or option names like {"{COLOUR}"}. Preview: {generateSkuFromPattern(skuPattern, { productSku, productName, optionCodes: { Colour: "BLK", Size: "M" } }, 0)}
              </p>
            </div>
            <div className="space-y-3">
              {options.map((o, i) => (
                <div key={o.id} className="grid grid-cols-1 sm:grid-cols-[160px_1fr_32px] gap-2 items-start">
                  <div>
                    <label className={labelCls}>Option {i + 1}</label>
                    <input value={o.name} onChange={(e) => updateOptionName(o.id, e.target.value)} className={inputCls} placeholder="e.g. Colour" />
                  </div>
                  <div>
                    <label className={labelCls}>Values (comma separated)</label>
                    <input value={draftValue(o)} onChange={(e) => setDrafts((d) => ({ ...d, [o.id]: e.target.value }))} onBlur={(e) => updateOptionValues(o.id, e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); updateOptionValues(o.id, e.currentTarget.value); } }} className={inputCls} placeholder="Black, White, Navy, Red, Green, Yellow, Orange, Purple, Pink, Grey" />
                  </div>
                  <div className="pt-5">
                    <button type="button" onClick={() => removeOption(o.id)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500" aria-label="Remove option">
                      <i className="ri-close-line" />
                    </button>
                  </div>
                </div>
              ))}
              <button type="button" onClick={addOption} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap">
                <i className="ri-add-line" /> Add another option dimension
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-slate-100">
              <div>
                <label className={labelCls}>Cost price (default)</label>
                <input type="number" step="0.01" min="0" value={sharedCost} onChange={(e) => setSharedCost(e.target.value)} className={inputCls} placeholder={defaultCost != null ? `Inherited ${money(defaultCost)}` : undefined} />
                <p className="text-[11px] text-slate-400 mt-1">Leave blank to inherit the product cost. Enter a value only when these variants have a different cost.</p>
              </div>
              <div><label className={labelCls}>Selling price (default)</label><input type="number" step="0.01" min="0" value={sharedSelling} onChange={(e) => setSharedSelling(e.target.value)} className={inputCls} placeholder="0.00" /></div>
              <div><label className={labelCls}>Weight kg (default)</label><input type="number" step="0.01" min="0" value={sharedWeight} onChange={(e) => setSharedWeight(e.target.value)} className={inputCls} placeholder="0.00" /></div>
            </div>
            {comboCount > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{comboCount.toLocaleString()} possible combination{comboCount === 1 ? "" : "s"}</span>
                {safety.warning && <span className="text-amber-600 text-xs"><i className="ri-alert-line mr-1" />{safety.warning}</span>}
              </div>
            )}
          </div>
        )}

        {activeStep === "preview" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">{includedCount.toLocaleString()} of {combinations.length.toLocaleString()} selected</p>
              <div className="flex items-center gap-2">
                <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search SKU or value..." className="w-48 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
                <button onClick={bulkActivate} className="px-2 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg whitespace-nowrap">Select all</button>
                <button onClick={bulkDeactivate} className="px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg whitespace-nowrap">Deselect all</button>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-slate-600">Bulk edit</p>
              <div className="flex flex-wrap gap-2">
                <input type="number" step="0.01" placeholder="Set all prices" className="w-28 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" onChange={(e) => bulkSetPrice(e.target.value)} />
                <input type="number" step="0.01" placeholder="Set all costs" className="w-28 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" onChange={(e) => bulkSetCost(e.target.value)} />
                <input type="number" step="0.01" placeholder="Weight kg" className="w-28 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" onChange={(e) => bulkSetWeight(e.target.value)} />
                <input type="number" step="0.1" placeholder="L" className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" value={bulkDims.l} onChange={(e) => setBulkDims((d) => ({ ...d, l: e.target.value }))} />
                <input type="number" step="0.1" placeholder="W" className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" value={bulkDims.w} onChange={(e) => setBulkDims((d) => ({ ...d, w: e.target.value }))} />
                <input type="number" step="0.1" placeholder="H" className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm" value={bulkDims.h} onChange={(e) => setBulkDims((d) => ({ ...d, h: e.target.value }))} />
                <button onClick={applyBulkDims} className="px-2 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg whitespace-nowrap border border-emerald-200">Apply dims</button>
                <select value={bulkStatus} onChange={(e) => { const v = e.target.value as "active" | "draft" | "paused"; setBulkStatus(v); bulkSetStatus(v); }} className="px-2 py-1.5 bg-white border border-slate-200 rounded text-sm text-slate-600">
                  <option value="active">Set all: Active</option>
                  <option value="draft">Set all: Draft</option>
                  <option value="paused">Set all: Paused</option>
                </select>
                <button onClick={applyPatternToAll} className="px-3 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg whitespace-nowrap border border-emerald-200">
                  <i className="ri-magic-line mr-1" />Apply SKU pattern
                </button>
              </div>
            </div>
            {dupCombos.length > 0 && <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700"><i className="ri-error-warning-line mr-1" />{dupCombos.length} duplicate combination(s). Exclude them.</div>}
            {dupSkus.length > 0 && <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700"><i className="ri-error-warning-line mr-1" />{dupSkus.length} duplicate SKU(s): {dupSkus.map((d) => d.sku).join(", ")}</div>}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="max-h-80 overflow-auto">
                <table className="w-full text-sm min-w-[820px]">
                  <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
                    <tr>
                      <th className="px-3 py-2 w-10"><input type="checkbox" checked={filteredCombinations.length > 0 && filteredCombinations.every((c) => c.included)} onChange={(e) => { const checked = e.target.checked; setCombinations((cs) => cs.map((c) => (filteredCombinations.includes(c) ? { ...c, included: checked } : c))); }} className="w-4 h-4 rounded accent-emerald-600" /></th>
                      <th className="px-3 py-2 font-medium">Variant</th>
                      <th className="px-3 py-2 font-medium">SKU</th>
                      <th className="px-3 py-2 font-medium">Barcode</th>
                      <th className="px-3 py-2 font-medium">Cost</th>
                      <th className="px-3 py-2 font-medium">Price</th>
                      <th className="px-3 py-2 font-medium">Weight</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {pagedCombinations.map((c) => {
                      const globalIdx = combinations.indexOf(c);
                      return (
                        <tr key={globalIdx} className={`hover:bg-slate-50 ${!c.included ? "opacity-50" : ""}`}>
                          <td className="px-3 py-2"><input type="checkbox" checked={c.included} onChange={() => toggleIncluded(globalIdx)} className="w-4 h-4 rounded accent-emerald-600" /></td>
                          <td className="px-3 py-2 text-slate-700">{c.values.join(" / ")}</td>
                          <td className="px-3 py-2"><input value={c.sku} onChange={(e) => setComboField(globalIdx, "sku", e.target.value)} className="w-32 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs font-mono" /></td>
                          <td className="px-3 py-2"><input value={c.barcode ?? ""} onChange={(e) => setComboField(globalIdx, "barcode", e.target.value === "" ? null : e.target.value)} className="w-32 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs font-mono" /></td>
                          <td className="px-3 py-2"><input type="number" step="0.01" min="0" value={c.cost ?? ""} onChange={(e) => setComboField(globalIdx, "cost", e.target.value === "" ? null : Number(e.target.value))} className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs" /></td>
                          <td className="px-3 py-2"><input type="number" step="0.01" min="0" value={c.price ?? ""} onChange={(e) => setComboField(globalIdx, "price", e.target.value === "" ? null : Number(e.target.value))} className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs" /></td>
                          <td className="px-3 py-2"><input type="number" step="0.01" min="0" value={c.weightKg ?? ""} onChange={(e) => setComboField(globalIdx, "weightKg", e.target.value === "" ? null : Number(e.target.value))} className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs" /></td>
                          <td className="px-3 py-2"><select value={c.status} onChange={(e) => setComboField(globalIdx, "status", e.target.value)} className="px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600"><option value="active">Active</option><option value="draft">Draft</option><option value="paused">Paused</option></select></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs text-slate-400">
                  {filteredCombinations.length === 0 ? "0 combinations" : `Showing ${safePage * PAGE_SIZE + 1}–${safePage * PAGE_SIZE + pagedCombinations.length} of ${filteredCombinations.length}`}
                </span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0} className="px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg disabled:opacity-40 whitespace-nowrap">Prev</button>
                  <span className="text-xs text-slate-500">{safePage + 1} / {totalPages}</span>
                  <button type="button" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1} className="px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg disabled:opacity-40 whitespace-nowrap">Next</button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeStep === "confirm" && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              You are about to create <span className="font-semibold text-slate-900">{includedCount.toLocaleString()}</span> new variant{includedCount === 1 ? "" : "s"} for <span className="font-semibold text-slate-900">{productName}</span> with normalized option records.
            </p>
            {dupCombos.length === 0 && dupSkus.length === 0 && (
              <div className="px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg text-xs text-emerald-700">
                <i className="ri-check-line mr-1" />
                No duplicate combinations or SKU collisions. Options will be created with stable IDs — safe to rename later.
              </div>
            )}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">Variant</th>
                      <th className="px-3 py-2 font-medium">SKU</th>
                      <th className="px-3 py-2 font-medium">Cost</th>
                      <th className="px-3 py-2 font-medium">Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {combinations.filter((c) => c.included).map((c, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2 text-slate-700">{c.values.join(" / ")}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-500">{c.sku}</td>
                        <td className="px-3 py-2 text-slate-600">{c.cost != null ? `$${c.cost.toFixed(2)}` : "—"}</td>
                        <td className="px-3 py-2 text-slate-600">{c.price != null ? `$${c.price.toFixed(2)}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
